import { realEstateData } from "./realEstateOwnersData.js";

/**
 * Get current language from localStorage
 */
function getCurrentLanguage() {
  return localStorage.getItem("lang") || "en";
}

/**
 * Format day display based on language
 */
function formatDayDisplay(date) {
  const lang = getCurrentLanguage();
  return date.toLocaleDateString(lang === "de" ? "de-DE" : "en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    weekday: "short"
  });
}

/**
 * Generate business days (Mon-Fri) for a given month
 */
function getBusinessDaysInMonth(year, month) {
  const businessDays = [];
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    const dayOfWeek = date.getDay();

    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
      businessDays.push(date);
    }
  }

  return businessDays;
}

/**
 * Generate 15-minute interval bookings for a single day
 */
function generateDayBookings(date, baseOccupancy, spaceBreakdown) {
  const bookings = [];
  const startHour = 8;
  const endHour = 19;

  for (let hour = startHour; hour < endHour; hour++) {
    for (let minute of [0, 15, 30, 45]) {
      const time = `${hour.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")}`;

      const hourProgress = (hour - startHour) / (endHour - startHour);
      const peakFactor = 1 - Math.abs(hourProgress - 0.5) * 0.4;

      const occupancyRate = Math.min(
        100,
        Math.max(30, baseOccupancy * peakFactor + (Math.random() - 0.5) * 10)
      );

      bookings.push({
        time,
        timestamp: new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          hour,
          minute
        ),
        occupancyRate: Math.round(occupancyRate * 10) / 10,
        peopleOnsite: Math.round((occupancyRate / 100) * 200),
        spaceUtilization: {
          openOffice:
            Math.round(
              (spaceBreakdown.openOffice + (Math.random() - 0.5) * 5) * 10
            ) / 10,
          meetingRoom:
            Math.round(
              (spaceBreakdown.meetingRoom + (Math.random() - 0.5) * 3) * 10
            ) / 10,
          lobby:
            Math.round(
              (spaceBreakdown.lobby + (Math.random() - 0.5) * 2) * 10
            ) / 10,
          conferenceRoom:
            Math.round(
              (spaceBreakdown.conferenceRoom + (Math.random() - 0.5) * 2) * 10
            ) / 10
        }
      });
    }
  }

  return bookings;
}

/**
 * Generate energy consumption for 15-minute intervals - MATCHES DAILY TOTAL
 */
function generateEnergyIntervals(date, dailyEnergyKwh, hourlyPattern) {
  const intervals = [];
  const startHour = 8;
  const endHour = 19;

  // Calculate pattern weights (how energy is distributed across hours)
  const totalPatternValue = hourlyPattern.reduce((sum, val) => sum + val, 0);
  const patternWeights = hourlyPattern.map((val) => val / totalPatternValue);

  // Generate distribution factors for each interval (with small variations)
  const intervalFactors = [];
  let factorSum = 0;

  for (let hour = startHour; hour < endHour; hour++) {
    const hourIndex = hour - startHour;
    const hourWeight = patternWeights[hourIndex] || 0;

    for (let minute of [0, 15, 30, 45]) {
      // Each interval gets 1/4 of the hour's weight, with small variation
      const variation = 0.95 + Math.random() * 0.1; // ±5% variation
      const factor = (hourWeight / 4) * variation;
      intervalFactors.push(factor);
      factorSum += factor;
    }
  }

  // Normalize factors so they sum to exactly 1
  const normalizedFactors = intervalFactors.map((f) => f / factorSum);

  // Generate intervals with exact distribution
  let index = 0;
  for (let hour = startHour; hour < endHour; hour++) {
    for (let minute of [0, 15, 30, 45]) {
      const time = `${hour.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")}`;

      const intervalKwh = dailyEnergyKwh * normalizedFactors[index];

      intervals.push({
        time,
        timestamp: new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate(),
          hour,
          minute
        ),
        electricityKwh: Math.round(intervalKwh * 0.85 * 10) / 10,
        gasKwh: Math.round(intervalKwh * 0.15 * 10) / 10,
        totalKwh: Math.round(intervalKwh * 10) / 10,
        cost: Math.round(intervalKwh * 0.28 * 100) / 100
      });

      index++;
    }
  }

  return intervals;
}

/**
 * Generate daily distribution factors that sum to exactly 1
 */
function generateDailyDistributionFactors(numDays) {
  // Generate base factors with variation
  const factors = [];
  let sum = 0;

  for (let i = 0; i < numDays; i++) {
    // Weekday pattern - slightly higher on mid-week days
    const dayOfWeek = i % 5; // 0=Mon, 4=Fri
    const weekdayWeight = 1 + Math.sin((dayOfWeek * Math.PI) / 4) * 0.1;

    // Add small random variation (±10%)
    const variation = 0.9 + Math.random() * 0.2;
    const factor = weekdayWeight * variation;

    factors.push(factor);
    sum += factor;
  }

  // Normalize so they sum to exactly 1
  return factors.map((f) => f / sum);
}

/**
 * Generate detailed daily data for a month - EXACT TOTALS
 */
function generateDetailedMonthData(monthData) {
  const [year, month] = monthData.month.split("-").map(Number);
  const businessDays = getBusinessDaysInMonth(year, month - 1);
  const numDays = businessDays.length;

  // Generate distribution factors for the month
  const dailyEnergyFactors = generateDailyDistributionFactors(numDays);
  const dailyCarbonFactors = generateDailyDistributionFactors(numDays);

  const dailyData = businessDays.map((date, dayIndex) => {
    // Distribute monthly totals across days using factors
    const dailyEnergyKwh = Math.round(
      monthData.energy.totalKwh * dailyEnergyFactors[dayIndex]
    );
    const dailyCost =
      Math.round(
        monthData.energy.totalCost * dailyEnergyFactors[dayIndex] * 100
      ) / 100;
    const dailyCarbon = Math.round(
      monthData.carbon.totalEmissions * dailyCarbonFactors[dayIndex]
    );

    // Calculate daily intensity based on actual daily energy
    const dailyIntensity = Math.round((dailyEnergyKwh / 10000) * 10) / 10; // per sqm

    // Small variations for other metrics (these don't need to sum exactly)
    const baseOccupancy = monthData.occupancy.rate + (Math.random() - 0.5) * 8;
    const dailyEsg = monthData.esg.overallScore + (Math.random() - 0.5) * 2;
    const dailyOperational =
      monthData.operational.energyAnalytics + (Math.random() - 0.5) * 3;

    return {
      date: date.toISOString().split("T")[0],
      displayDate: formatDayDisplay(date),
      dayOfWeek: date.toLocaleDateString("en-US", { weekday: "long" }),
      month: monthData.month,
      year: year,

      // 44 bookings per day (11 hours × 4 intervals)
      bookings: generateDayBookings(
        date,
        baseOccupancy,
        monthData.occupancy.floorplanBreakdown
      ),

      // Energy intervals - will sum to dailyEnergyKwh
      energyIntervals: generateEnergyIntervals(
        date,
        dailyEnergyKwh,
        monthData.energy.hourlyUsage.electricity
      ),

      // Daily summary with ALL KPIs
      dailySummary: {
        // Energy KPIs
        energy: {
          intensity: dailyIntensity,
          totalKwh: dailyEnergyKwh,
          totalCost: dailyCost,
          peakKwh: Math.round((dailyEnergyKwh / 44) * 1.3 * 10) / 10,
          topAppliancesShare:
            monthData.energy.topAppliancesShare + (Math.random() - 0.5) * 2,
          activeSpaces: monthData.energy.activeSpaces
        },

        // Carbon KPIs
        carbon: {
          totalEmissions: dailyCarbon,
          gaugeValue: monthData.carbon.gaugeValue + (Math.random() - 0.5) * 2,
          floorBreakdown: monthData.carbon.floorBreakdown
        },

        // ESG KPIs
        esg: {
          overallScore: Math.round(dailyEsg * 10) / 10,
          gaugeValue: Math.round(dailyEsg * 10) / 10,
          spaceAllocation: monthData.esg.spaceAllocation,
          compliance: monthData.esg.compliance
        },

        // Occupancy KPIs
        occupancy: {
          rate: Math.round(baseOccupancy * 10) / 10,
          peak: Math.round(baseOccupancy * 1.15 * 10) / 10,
          peopleOnsite: Math.round((baseOccupancy / 100) * 200),
          totalCapacity: 200,
          donutData: {
            occupied: Math.round(baseOccupancy),
            free: Math.round(100 - baseOccupancy)
          },
          floorplanBreakdown: monthData.occupancy.floorplanBreakdown,
          spaceUtilization:
            monthData.occupancy.spaceUtilization + (Math.random() - 0.5) * 3
        },

        // Operational KPIs
        operational: {
          energyAnalytics: Math.round(dailyOperational * 10) / 10,
          dynamicOccupancy: Math.round(baseOccupancy * 10) / 10,
          utilizationAnalytics: Math.round(baseOccupancy * 10) / 10,
          spaceEfficiency: monthData.operational.spaceEfficiency,
          costPerSqm: monthData.operational.costPerSqm
        }
      }
    };
  });

  // Calculate verification totals
  const calculatedTotals = {
    energyKwh: dailyData.reduce(
      (sum, day) => sum + day.dailySummary.energy.totalKwh,
      0
    ),
    energyCost:
      Math.round(
        dailyData.reduce(
          (sum, day) => sum + day.dailySummary.energy.totalCost,
          0
        ) * 100
      ) / 100,
    carbonEmissions: dailyData.reduce(
      (sum, day) => sum + day.dailySummary.carbon.totalEmissions,
      0
    )
  };

  return {
    month: monthData.month,
    displayMonth: monthData.displayMonth,
    year: year,
    businessDaysCount: businessDays.length,
    totalDataPoints: businessDays.length * 44,
    dailyData,

    // Monthly summary (from original data)
    monthlySummary: {
      energy: monthData.energy,
      carbon: monthData.carbon,
      esg: monthData.esg,
      occupancy: monthData.occupancy,
      operational: monthData.operational
    },

    // Verification - should match exactly
    verification: {
      expected: {
        energyKwh: monthData.energy.totalKwh,
        energyCost: monthData.energy.totalCost,
        carbonEmissions: monthData.carbon.totalEmissions
      },
      calculated: calculatedTotals,
      matches: {
        energyKwh: calculatedTotals.energyKwh === monthData.energy.totalKwh,
        energyCost:
          Math.abs(calculatedTotals.energyCost - monthData.energy.totalCost) <
          1,
        carbonEmissions:
          calculatedTotals.carbonEmissions === monthData.carbon.totalEmissions
      },
      businessDays: businessDays.length
    }
  };
}

/**
 * Get detailed data for all months or specific month
 */
export function getDetailedRealEstateData(monthString = null) {
  if (monthString) {
    const monthData = realEstateData.monthlyData.find(
      (m) => m.month === monthString
    );
    if (!monthData) return null;
    return generateDetailedMonthData(monthData);
  }

  return realEstateData.monthlyData.map((monthData) =>
    generateDetailedMonthData(monthData)
  );
}

/**
 * Get all bookings across all months
 */
export function getAllBookings() {
  const allMonthsDetailed = getDetailedRealEstateData();

  const allBookings = [];
  allMonthsDetailed.forEach((month) => {
    month.dailyData.forEach((day) => {
      day.bookings.forEach((booking) => {
        allBookings.push({
          ...booking,
          date: day.date,
          month: month.month,
          year: month.year
        });
      });
    });
  });

  return allBookings;
}

/**
 * Get statistics
 */
export function getDetailedDataStats() {
  const allMonthsDetailed = getDetailedRealEstateData();

  const totalDays = allMonthsDetailed.reduce(
    (sum, m) => sum + m.businessDaysCount,
    0
  );
  const totalDataPoints = allMonthsDetailed.reduce(
    (sum, m) => sum + m.totalDataPoints,
    0
  );

  return {
    totalMonths: allMonthsDetailed.length,
    totalBusinessDays: totalDays,
    totalBookingIntervals: totalDataPoints,
    intervalsPerDay: 44,
    intervalDuration: "15 minutes",
    operatingHours: "08:00 - 19:00",
    dateRange: {
      start: allMonthsDetailed[0]?.month,
      end: allMonthsDetailed[allMonthsDetailed.length - 1]?.month
    }
  };
}

/**
 * Filter data by year, month, and/or day
 */
export function filterDetailedData(filters = {}) {
  const { year, month, day } = filters;
  let allData = getDetailedRealEstateData();

  // Filter by year
  if (year) {
    allData = allData.filter((m) => m.year === parseInt(year));
  }

  // Filter by month
  if (month) {
    allData = allData.filter((m) => m.month === month);
  }

  // Flatten to daily data
  let dailyData = [];
  allData.forEach((monthData) => {
    dailyData.push(...monthData.dailyData);
  });

  // Filter by specific day
  if (day) {
    dailyData = dailyData.filter((d) => d.date === day);
  }

  return dailyData;
}

/**
 * Get available years
 */
export function getAvailableYears() {
  const allData = getDetailedRealEstateData();
  const years = [...new Set(allData.map((m) => m.year))];
  return years.sort();
}

/**
 * Get available months for a year
 */
export function getAvailableMonths(year = null) {
  let allData = getDetailedRealEstateData();

  if (year) {
    allData = allData.filter((m) => m.year === parseInt(year));
  }

  return allData.map((m) => ({
    value: m.month,
    text: m.displayMonth,
    year: m.year
  }));
}

/**
 * Get available days for a month
 */
export function getAvailableDays(monthString) {
  const monthData = getDetailedRealEstateData(monthString);

  if (!monthData) return [];

  return monthData.dailyData.map((d) => ({
    value: d.date,
    text: d.displayDate,
    dayOfWeek: d.dayOfWeek
  }));
}

/**
 * Verify data integrity - check if detailed totals match monthly summaries
 */
export function verifyDataIntegrity() {
  const allMonths = getDetailedRealEstateData();

  const results = allMonths.map((month) => ({
    month: month.month,
    verification: month.verification
  }));

  console.table(results);
  return results;
}
